import { Box, Checkbox as CheckboxInput, Text, TextField } from "@engaging-tech/components"
import React, { useCallback, useEffect, useMemo, useState } from "react"

import { isChoiceVisible } from "../../../../utils/logic"

const Checkbox = ({
  values: selectInputs,
  onAnswer,
  initialValue,
  allAnswers,
  choiceLogics,
  allowOther,
  validation
}) => {
  const actualValues = selectInputs || []

  const otherOption = useMemo(() => {
    if (!allowOther) return null
    if (!actualValues || actualValues.length === 0) {
      return { id: "other", value: "Other" }
    }
    let found = actualValues.find(
      val =>
        val.value?.toLowerCase().includes("other") ||
        val.value?.toLowerCase() === "other" ||
        val.id?.toLowerCase().includes("other")
    )
    if (!found) {
      return { id: "other", value: "Other" }
    }
    return found
  }, [allowOther, actualValues])

  const [selected, setSelected] = useState([])
  const [otherText, setOtherText] = useState("")

  const isSelected = value => selected.includes(value)

  const updateAnswer = useCallback(
    (selectedValues, otherValue) => {
      const isOtherSelected = otherOption && selectedValues.includes(otherOption.id)
      const otherTextValue = typeof otherValue === 'string' ? otherValue : ""

      // Remove duplicates from selectedValues
      const uniqueValues = [...new Set(selectedValues)]

      const answer = {
        values: uniqueValues,
        otherValues: isOtherSelected ? [{ optionId: otherOption.id, value: otherTextValue }] : []
      }
      onAnswer(answer)
    },
    [otherOption, onAnswer]
  )

  const handleToggle = useCallback(
    value => {
      let newSelected
      let newOtherText = otherText

      if (otherOption && value === otherOption.id) {
        if (selected.includes(otherOption.id)) {
          newSelected = selected.filter(id => id !== otherOption.id)
          newOtherText = ""
        } else {
          // Remove duplicates before adding
          const uniqueSelected = [...new Set(selected)]
          newSelected = [...uniqueSelected, otherOption.id]
        }
      } else {
        const uniqueSelected = [...new Set(selected)]
        newSelected = uniqueSelected.includes(value) 
          ? uniqueSelected.filter(id => id !== value) 
          : [...uniqueSelected, value]
      }

      setSelected(newSelected)
      if (otherOption && value === otherOption.id && selected.includes(otherOption.id)) {
        setOtherText("")
      }
      updateAnswer(newSelected, newOtherText)
    },
    [otherOption, otherText, selected, updateAnswer]
  )

  const handleOtherTextChange = useCallback(
    e => {
      const text = e?.target?.value ?? ""
      const textValue = typeof text === 'string' ? text : ""
      setOtherText(textValue)
      updateAnswer(selected, textValue)
    },
    [selected, updateAnswer]
  )

  useEffect(() => {
    if (initialValue) {
      if (Array.isArray(initialValue.value)) {
        const normalValues = initialValue.value.filter(val => {
          if (typeof val === "string" && otherOption && val !== otherOption.id) {
            return !actualValues?.some(opt => opt.id === val)
          }
          return true
        })
        const uniqueNormalValues = [...new Set(normalValues)]
        setSelected(uniqueNormalValues)

        const otherTextValue = initialValue.value.find(
          val => typeof val === "string" && !actualValues?.some(opt => opt.id === val)
        )
        if (otherTextValue && otherOption) {
          setOtherText(otherTextValue)
          const finalValues = [...new Set([...uniqueNormalValues, otherOption.id])]
          setSelected(finalValues)
        }
      } else if (initialValue.value?.values) {
        const baseValues = [...new Set(initialValue.value.values || [])]
        if (initialValue.value.otherValues && initialValue.value.otherValues.length > 0 && otherOption) {
          setOtherText(initialValue.value.otherValues[0].value || "")
          const finalValues = [...new Set([...baseValues, otherOption.id])]
          setSelected(finalValues)
        } else {
          setSelected(baseValues)
        }
      }
    }
  }, [initialValue, actualValues, otherOption])

  const isOtherSelected = otherOption && isSelected(otherOption.id)

  const normalValues = useMemo(() => {
    return actualValues.filter(val => !otherOption || val.id !== otherOption.id)
  }, [actualValues, otherOption])

  const otherLimit = validation?.limit ?? 250

  if (!actualValues || actualValues.length === 0) {
    return null
  }

  return (
    <Box mt={[3, 5]}>
      {normalValues.map(val => {
        if (choiceLogics && !isChoiceVisible(val, allAnswers, choiceLogics)) {
          return null
        }
        return (
          <CheckboxInput
            key={val.id}
            id={val.id}
            checked={isSelected(val.id)}
            onChange={() => handleToggle(val.id)}
            label={() => <Text>{val.value}</Text>}
            colour="dark.0"
            mb={2}
          />
        )
      })}

      {allowOther && otherOption && (
        <>
          <CheckboxInput
            id={otherOption.id}
            checked={isOtherSelected}
            onChange={() => handleToggle(otherOption.id)}
            label={() => <Text>{otherOption.value || "Other"}</Text>}
            colour="dark.0"
            mb={2}
          />

          <Box mb={2} mt={2} ml={0} pr={3}>
            <TextField
              placeholder="Please specify..."
              value={typeof otherText === 'string' ? otherText : ""}
              onChange={handleOtherTextChange}
              disabled={!isOtherSelected}
              autoResize
              minHeight={60}
              rows={1}
              maxLength={otherLimit}
            />
          </Box>
        </>
      )}
    </Box>
  )
}

export default Checkbox
